import config from '../config';
import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import "../style/createLocation.css";
import { isUUID } from "validator";
import { useAuth } from "../context/authContext";
import Button from "../components/Button";

const Location = () => {
  const { authData } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [showPopup, setShowPopup] = useState(false);
  const [popupMessage, setPopupMessage] = useState("");
  const [popupType, setPopupType] = useState("success");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [initialFormData, setInitialFormData] = useState(null);
  const formRef = React.useRef();
  const todayDate = new Date().toISOString().split("T")[0];

  const location = useLocation();
  const [title, setTitle] = useState("Create Location");

  // Get pre-filled data from navigation state
  const locationData = location.state?.item || null;
  const { accountId, editMode } = location.state || {};
  const [formData, setFormData] = useState({
    id: "",
    name: "",
    city: "",
    state: "",
    country: "",
    joinedDate: "",
    notes: "",
    active: false,
  });

  useEffect(() => {
    if (editMode) {
      setTitle("Edit Location");
    }
  }, [editMode]);

  // Store initial form data when editing
  useEffect(() => {
    if (editMode && !loading && !initialFormData) {
      setInitialFormData(formData);
    }
  }, [editMode, loading, formData, initialFormData]);

  const hasFormChanges = () => {
    if (!editMode || !initialFormData) return true;
    return JSON.stringify(formData) !== JSON.stringify(initialFormData);
  };

  const isFormValid = () => {
    if (editMode) return hasFormChanges();
    return (
      formData.name.trim() !== "" &&
      formData.city.trim() !== "" &&
      formData.country.trim() !== ""
    );
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  useEffect(() => {
    const todayDate = new Date().toISOString().split("T")[0];

    if (locationData) {
      const dateValue = locationData.joinedDate || "";

      let formattedDate = "";
      if (dateValue) {
        try {
          const date = new Date(dateValue);
          if (!isNaN(date.getTime())) {
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, "0");
            const day = String(date.getDate()).padStart(2, "0");
            formattedDate = `${year}-${month}-${day}`;
          } else {
            console.error("Invalid date:", dateValue);
          }
        } catch (error) {
          console.error("Error parsing date:", error);
        }
      }

      setFormData({
        id: locationData.id || "",
        name: locationData.name || "",
        city: locationData.city || "",
        state: locationData.state || "",
        country: locationData.country || "",
        joinedDate: formattedDate,
        notes: locationData.notes || "",
      active: locationData.isActive ?? locationData.active ?? false,
      });
      setLoading(false);
    } else if (id) {
      fetchLocationData();
    } else {
      setFormData((prev) => ({
        ...prev,
        joinedDate: todayDate,
      }));
      setLoading(false);
    }
  }, [id, locationData]);

  const fetchLocationData = async () => {
    if (!isUUID(id)) {
      console.error(`Invalid UUID: ${id}`);
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(
        `${config.BASE_URL}/locations/${id}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${authData.accessToken}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch location data");
      }

      const locationData = await response.json();
      const dateValue = locationData.joinedDate || "";

      let formattedDate = "";
      if (dateValue) {
        try {
          const date = new Date(dateValue);
          if (!isNaN(date.getTime())) {
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, "0");
            const day = String(date.getDate()).padStart(2, "0");
            formattedDate = `${year}-${month}-${day}`;
          } else {
            console.error("Invalid date:", dateValue);
          }
        } catch (error) {
          console.error("Error parsing date:", error);
        }
      }

      setFormData({
        id: locationData.id || "",
        name: locationData.name || "",
        city: locationData.city || "",
        state: locationData.state || "",
        country: locationData.country || "",
        joinedDate: formattedDate,
        notes: locationData.notes || "",
   active: locationData.isActive ?? locationData.active ?? false,
      });
    } catch (error) {
      console.error("Error fetching location data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isFormValid() || isSubmitting) return;

    setIsSubmitting(true);

    try {
      const payload = {
        ...formData,
         active:   formData.active,  
        AccountId: accountId,
        joinedDate: formData.joinedDate?.trim() ? formData.joinedDate : null,
      };

      const isUpdate = !!formData.id;
      const method = isUpdate ? "PUT" : "POST";
      const url = isUpdate
        ? `${config.BASE_URL}/locations/${formData.id}`
        : `${config.BASE_URL}/locations`;

      const response = await fetch(url, {
        method: method,
        headers: {
          Authorization: `Bearer ${authData.accessToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        await response.json();

        setPopupMessage(
          isUpdate
            ? "Location updated successfully!"
            : "Location saved successfully!"
        );
        setPopupType("success");
        setShowPopup(true);

        setTimeout(() => {
          setShowPopup(false);
          navigate(`/organization/${location.state?.organizationId}`, {
            state: {
              expandedAccountId: accountId,
              locationMessage: "Location saved successfully!",
            },
          });
          
        }, 4000);
      } else {
        console.error("Failed to save location");
        setPopupMessage(
          isUpdate ? "Error updating location!" : "Error saving location!"
        );
        setPopupType("error");
        setShowPopup(true);
        setTimeout(() => {
          setShowPopup(false);
          setIsSubmitting(false);
        }, 4000);
      }
    } catch (error) {
      console.error("Error creating or updating location:", error);
      const isUpdate = !!formData.id;
      setPopupMessage(
        isUpdate ? "Error updating location!" : "Error saving location!"
      );
      setPopupType("error");
      setShowPopup(true);
      setTimeout(() => {
        setShowPopup(false);
        setIsSubmitting(false);
      }, 4000);
    }
  };

  const handleBackClick = () => {
    navigate(-1);
  };

  return (
    <div className="location-page">
      {showPopup && <div className={`popup-${popupType}`}>{popupMessage}</div>}

      <div className="location-content-wrapper">
        <div className="location-header">
          <Button
            className="location-back-button"
            onClick={handleBackClick}
            text="← Back"
            color="#006d7d"
          />
          <h1>{title}</h1>
        </div>
        <div className="location-form-container">
          <form ref={formRef} onSubmit={handleSubmit}>
            <div className="location-form-group">
              <label>Name *</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
              />
            </div>
            <div className="location-form-group">
              <label>City *</label>
              <input
                type="text"
                name="city"
                value={formData.city}
                onChange={handleInputChange}
                required
              />
            </div>
            <div className="location-form-group">
              <label>State/Province/Region</label>
              <input
                type="text"
                name="state"
                value={formData.state}
                onChange={handleInputChange}
              />
            </div>
            <div className="location-form-group">
              <label>Country *</label>
              <input
                type="text"
                name="country"
                value={formData.country}
                onChange={handleInputChange}
                required
              />
            </div>
            <div className="location-form-group">
              <label>Entered Date</label>
              <input
                type="date"
                name="joinedDate"
                value={formData.joinedDate || ""}
                onChange={handleInputChange}
                {...(!editMode && { min: todayDate })}
              />
            </div>
            <div className="location-form-group">
              <label>Notes</label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleInputChange}
              />
            </div>
            <div className="location-form-group is-active-wrapper">
              <label>Active</label>
              <input
                type="checkbox"
                name="active"
                checked={formData.active}
                onChange={handleInputChange}
              />
            </div>
            <div className="location-save-button-container">
              <Button
                type="submit"
                className="location-save-button"
                text={isSubmitting ? "Saving..." : "Save"}
                color="#006d7d"
                disabled={!isFormValid() || isSubmitting}
                onClick={() => {
                  const form = formRef.current;
                  const isUpdate = !!formData.id;

                  if (form && !form.checkValidity()) {
                    form.reportValidity();

                    setPopupMessage(
                      isUpdate
                        ? "Error updating location!"
                        : "Error saving location!"
                    );
                    setPopupType("error");
                    setShowPopup(true);
                    setTimeout(() => setShowPopup(false), 4000);
                  }
                }}
              />
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Location;